import { createAction } from "@reduxjs/toolkit";

import { OrganizationProjectStatusesTypes } from "Api/organizations/projects/OrganizationProjects.types";
import { loadEnvironments } from "Reducers/environment";
import { addIntegration, getIntegrations } from "Reducers/integration";
import {
  extractStatusCode,
  loadOneProject,
  PROJECT_NOT_FOUND
} from "Reducers/project/project";
// Deep imports, not the `Reducers/projectCreation` barrel: the barrel re-exports
// every thunk, and `thunks/fetchVerificationType.thunk.ts` imports
// `Reducers/organization/organization` and `Reducers/organization/trial` — back
// into the module this file belongs to. The selectors and the one thunk taken
// here are leaves (see projects.selectors.ts:11).
import { selectGitProvider } from "Reducers/projectCreation/projectCreation.selectors";
import { isGitProvider } from "Reducers/projectCreation/projectCreation.types";
import { createProject } from "Reducers/projectCreation/thunks/createProject.thunk";
import { createProjectFromGithub } from "Reducers/projectCreation/thunks/createProjectFromGithub.thunk";
import {
  startAppListening,
  type AppStartListening
} from "Store/listenerMiddleware";

import { selectHasGitIntegration } from "./projects.selectors";
import {
  clearIntegrationActivitiesSeen,
  clearPendingGithubIntegration,
  clearProvisioningSlot,
  clearWizardGitProvider,
  markIntegrationActivitiesSeen,
  markPendingGithubIntegration,
  markRegionalRetryEnd,
  markRegionalRetryStart,
  setWizardGitProvider
} from "./projects.slice";

import type { GitProvider } from "Reducers/projectCreation/projectCreation.types";
import type { RootState } from "Store/configureStore";

const PENDING_STORAGE_KEY = "pendingGithubProject";
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;
const CONNECT_STORAGE_KEY = "pendingProjectConnect";
const CONNECT_TTL_MS = 24 * 60 * 60 * 1000;
const POLL_INTERVAL_MS = 5_000;
const ACTIVITY_DEBOUNCE_MS = 10_000;
const REGIONAL_RETRY_DELAY_MS = 2_500;
const REGIONAL_RETRY_MAX_ATTEMPTS = 6;

// `loadOneProject` rejects with either `{ code: 404 }` (Project.get threw) or
// the bare `PROJECT_NOT_FOUND` string (Project.get returned undefined after
// retries). Both signal the same regional not-found condition and should drive
// the same retry.
const isRegionalNotFound = (payload: unknown): boolean =>
  payload === PROJECT_NOT_FOUND || extractStatusCode(payload) === 404;

const removeStoredRecord = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage access denied (private mode / iframe sandbox). Nothing to do.
  }
};

/**
 * Reads and parses one of the records below.
 *
 * Only the parse is shared. The two records agree on being single-slot and
 * short-lived and on nothing else — what makes one valid, and when it may be
 * evicted, is its own business — so each keeps its own checks. A store that
 * cannot be read and a value that cannot be parsed both answer "nothing there";
 * the second evicts, because it can only fail the same way again.
 *
 * `Partial` because the caller is reading whatever a previous version of this
 * code, or another tab, happened to write.
 */
const readStoredRecord = <T>(key: string): Partial<T> | undefined => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as Partial<T>;
  } catch {
    removeStoredRecord(key);
    return undefined;
  }
};

const removePendingFlag = () => removeStoredRecord(PENDING_STORAGE_KEY);

/**
 * The provider the user picked on the create page, held for the one project
 * they picked it for. A single slot rather than a map, matching
 * `pendingGithubProject`: creating a second project before connecting the
 * first overwrites it, and that project falls back to the selection panel —
 * the behaviour it would have had anyway.
 */
type ConnectRecord = {
  projectId: string;
  provider: NonNullable<GitProvider>;
  timestamp: number;
};

const removeConnectRecord = () => removeStoredRecord(CONNECT_STORAGE_KEY);

const readConnectRecord = (): ConnectRecord | undefined => {
  const parsed = readStoredRecord<ConnectRecord>(CONNECT_STORAGE_KEY);
  if (!parsed) return undefined;

  // Anything unusable in itself is evicted here — a record that survives one
  // read only to be re-parsed and re-rejected on the next is just litter.
  if (!parsed.projectId || !isGitProvider(parsed.provider)) {
    removeConnectRecord();
    return undefined;
  }
  if (Date.now() >= (parsed.timestamp ?? 0) + CONNECT_TTL_MS) {
    removeConnectRecord();
    return undefined;
  }

  return parsed as ConnectRecord;
};

/**
 * Removes the record only when it belongs to the given project. The unqualified
 * `removeConnectRecord` would throw away a *different* project's pending choice
 * — the divergence from `pendingGithubProject`, which evicts on any mismatch
 * because it only ever runs for the project the user just created.
 */
const removeConnectRecordFor = (projectId: string) => {
  if (readConnectRecord()?.projectId === projectId) removeConnectRecord();
};

/**
 * Coordination action dispatched by the consumer when the underlying
 * in-progress integration activity flips state. The listener turns the
 * transition into a sticky `seenIntegrationActivities` flag with a 10s
 * debounce on clear — so the phase selector doesn't flash between
 * `integration.fetch` completing and `environment.push` starting.
 */
export const setIntegrationActivityState = createAction<{
  projectId: string;
  isLoading: boolean;
}>("app/organization/projects/setIntegrationActivityState");

/**
 * Reads the `pendingGithubProject` flag from localStorage and dispatches
 * `markPendingGithubIntegration` iff it matches the given project and is
 * still inside the 24h freshness window. Stale entries are evicted from
 * storage.
 */
export const loadPendingGithubIntegrationFromStorage = createAction<{
  projectId: string;
}>("app/organization/projects/loadPendingGithubIntegrationFromStorage");

/**
 * Reads the `pendingProjectConnect` record and seeds `wizardGitProviders` iff
 * it names the given project and is still inside the 24h window, so the wizard
 * opens on the chosen provider after `?connect=` has fallen off the URL.
 */
export const loadWizardGitProviderFromStorage = createAction<{
  projectId: string;
}>("app/organization/projects/loadWizardGitProviderFromStorage");

/**
 * The user rejecting a restored choice. Forgets it for good rather than for
 * this render: while the record survives, it re-seeds the panel on the next
 * visit and the exit button looks dead.
 */
export const forgetWizardGitProvider = createAction<{
  projectId: string;
}>("app/organization/projects/forgetWizardGitProvider");

/**
 * Kicks off a bounded retry loop for `loadOneProject` after a 404 from the
 * regional cluster while the org-scoped project has flipped to `active`.
 * This handles the post-creation race where central replication lands before
 * regional. Dispatched only by the rejection listener; not a public API.
 */
export const triggerPostProvisioningRegionalRetry = createAction<{
  organizationId: string;
  projectId: string;
}>("app/organization/projects/triggerPostProvisioningRegionalRetry");

/**
 * Registers every listener defined in this file against the given
 * `startListening`. The production store calls this via the singleton
 * `startAppListening`; tests register against a fresh listener middleware.
 */
export const registerProjectsListeners = (
  startListening: AppStartListening
) => {
  // --------------------------------------------------------------------------
  // Pending GitHub integration polling
  // --------------------------------------------------------------------------

  /**
   * Marks a project created from a GitHub repository as waiting for the
   * integration SIA is building for it. The project page reads this back on
   * mount and holds the loader until the integration arrives; without it the
   * wizard offers the integration picker instead, and the user can start a
   * second, token-based integration on top of the one still being created.
   *
   * Keyed on the thunk rather than written from the two components that call
   * it, like the provider carry-over below. `Details` wrote it and `Verify`
   * did not, so the users who need card verification — the only ones who go
   * through `Verify` — were the ones who got the picker.
   */
  startListening({
    actionCreator: createProjectFromGithub.fulfilled,
    effect: action => {
      const { projectId } = action.payload;
      if (!projectId) return;

      try {
        localStorage.setItem(
          PENDING_STORAGE_KEY,
          JSON.stringify({ projectId, timestamp: Date.now() })
        );
      } catch {
        // Storage access denied. The wizard opens on the picker, which is the
        // behaviour this flag exists to improve on, not one it breaks.
      }
    }
  });

  startListening({
    actionCreator: loadPendingGithubIntegrationFromStorage,
    effect: (action, listenerApi) => {
      const { projectId } = action.payload;

      const parsed = readStoredRecord<{ projectId: string; timestamp: number }>(
        PENDING_STORAGE_KEY
      );
      if (!parsed) return;

      // A record with no timestamp is already expired, which evicts it below.
      const expiresAt = (parsed.timestamp ?? 0) + PENDING_TTL_MS;
      if (parsed.projectId !== projectId || Date.now() >= expiresAt) {
        removePendingFlag();
        return;
      }

      listenerApi.dispatch(
        markPendingGithubIntegration({ projectId, expiresAt })
      );
    }
  });

  /**
   * Polls `getIntegrations` until the GitHub App integration appears, the
   * TTL passes, or `clearPendingGithubIntegration` is dispatched.
   *
   * `cancelActiveListeners` means a fresh `markPendingGithubIntegration`
   * supersedes the previous poll loop — there is only ever one poller
   * active at a time, which matches the single-route nature of the UI.
   */
  startListening({
    actionCreator: markPendingGithubIntegration,
    effect: async (action, listenerApi) => {
      listenerApi.cancelActiveListeners();

      const { projectId, expiresAt } = action.payload;

      // If the integration is already present at the moment we start, drop
      // the pending state immediately.
      if (selectHasGitIntegration(listenerApi.getState(), { projectId })) {
        removePendingFlag();
        listenerApi.dispatch(clearPendingGithubIntegration({ projectId }));
        return;
      }

      while (Date.now() < expiresAt) {
        await listenerApi.delay(POLL_INTERVAL_MS);
        if (listenerApi.signal.aborted) return;
        if (Date.now() >= expiresAt) break;

        await listenerApi.dispatch(getIntegrations({ projectId }));
        if (listenerApi.signal.aborted) return;

        if (selectHasGitIntegration(listenerApi.getState(), { projectId })) {
          removePendingFlag();
          listenerApi.dispatch(clearPendingGithubIntegration({ projectId }));
          return;
        }
      }

      // TTL passed without finding the integration.
      removePendingFlag();
      listenerApi.dispatch(clearPendingGithubIntegration({ projectId }));
    }
  });

  /**
   * When the GitHub App integration arrives via a `getIntegrations` fetch
   * outside the poll loop (e.g. the initial mount fetch), clear the pending
   * flag so the phase selector moves on without waiting for the next poll.
   */
  startListening({
    actionCreator: getIntegrations.fulfilled,
    effect: (action, listenerApi) => {
      const { projectId } = action.meta.arg;
      const state = listenerApi.getState();
      if (!state.organizationProjects.pendingGithubIntegrations[projectId]) {
        return;
      }
      if (selectHasGitIntegration(state, { projectId })) {
        removePendingFlag();
        listenerApi.dispatch(clearPendingGithubIntegration({ projectId }));
      }
    }
  });

  // --------------------------------------------------------------------------
  // Create-page provider carry-over
  // --------------------------------------------------------------------------

  /**
   * Records the provider the user picked on the create page against the project
   * that was just created. `?connect=` on the project URL is still the primary
   * carrier — it is addressable and shareable — but it is lost the moment the
   * user navigates away and comes back, and provisioning takes long enough that
   * leaving and returning is the expected thing to do.
   *
   * Keyed on the thunk rather than written from the two components that call it,
   * so `Details` and `Verify` need no code of their own and cannot disagree.
   * `createProjectFromGithub` is deliberately not covered: SIA creates that
   * integration itself, so there is no choice to carry.
   */
  startListening({
    actionCreator: createProject.fulfilled,
    effect: (action, listenerApi) => {
      const { projectId } = action.payload;
      if (!projectId) return;

      // Null for the demo route, which picks no provider.
      const provider = selectGitProvider(listenerApi.getState(), {
        organizationId: action.meta.arg.organizationId
      });
      if (!provider) return;

      listenerApi.dispatch(setWizardGitProvider({ projectId, provider }));

      try {
        localStorage.setItem(
          CONNECT_STORAGE_KEY,
          JSON.stringify({ projectId, provider, timestamp: Date.now() })
        );
      } catch {
        // Storage access denied. The param still carries this hop; only the
        // return visit loses the choice, which is today's behaviour.
      }
    }
  });

  startListening({
    actionCreator: loadWizardGitProviderFromStorage,
    effect: (action, listenerApi) => {
      const { projectId } = action.payload;

      const record = readConnectRecord();
      // A record for another project is somebody else's pending choice and has
      // to survive this read — see `removeConnectRecordFor`.
      if (!record || record.projectId !== projectId) return;

      // In-app navigation keeps the integration slice, so a project connected
      // earlier this session is already known to be connected here. Seeding
      // anyway would flash the panel over the configuration phase for as long
      // as the integrations refetch takes.
      if (selectHasGitIntegration(listenerApi.getState(), { projectId })) {
        removeConnectRecord();
        return;
      }

      listenerApi.dispatch(
        setWizardGitProvider({ projectId, provider: record.provider })
      );
    }
  });

  startListening({
    actionCreator: forgetWizardGitProvider,
    effect: (action, listenerApi) => {
      const { projectId } = action.payload;
      removeConnectRecordFor(projectId);
      listenerApi.dispatch(clearWizardGitProvider({ projectId }));
    }
  });

  /**
   * The choice is spent the moment the integration is created — this is the
   * panel the user was steered to succeeding at its job.
   */
  startListening({
    actionCreator: addIntegration.fulfilled,
    effect: (action, listenerApi) => {
      const projectId = action.meta.arg.project.id;
      removeConnectRecordFor(projectId);
      listenerApi.dispatch(clearWizardGitProvider({ projectId }));
    }
  });

  /**
   * And the same when the integration turns up from a fetch rather than from
   * this browser creating it — connected from the CLI, by a colleague, or in
   * another tab. Without this the record would sit there until its TTL and
   * re-open the panel if the integration were later deleted.
   */
  startListening({
    actionCreator: getIntegrations.fulfilled,
    effect: (action, listenerApi) => {
      const { projectId } = action.meta.arg;
      const state = listenerApi.getState();
      if (!state.organizationProjects.wizardGitProviders[projectId]) return;
      if (!selectHasGitIntegration(state, { projectId })) return;

      removeConnectRecordFor(projectId);
      listenerApi.dispatch(clearWizardGitProvider({ projectId }));
    }
  });

  // --------------------------------------------------------------------------
  // Integration activity debounce
  // --------------------------------------------------------------------------

  // --------------------------------------------------------------------------
  // Post-stream completion handshake
  // --------------------------------------------------------------------------

  /**
   * When the regional project or its environments land, drop the provisioning
   * slot if the project's other data is also ready. The slot is intentionally
   * kept alive past the stream's `done` event and past `getProject.fulfilled`
   * so the panel keeps painting the completed timeline through the regional
   * fetch window — without this listener, the panel unmounts the instant the
   * org-projects status flips and the generic "Upsun engine initializing"
   * loader leaks through.
   */
  const tryClearSlot = (
    state: RootState,
    organizationId: string | undefined,
    projectId: string | undefined
  ) => {
    if (!organizationId || !projectId) return undefined;
    const orgProject =
      state.organizationProjects.data?.[organizationId]?.[projectId];
    if (!orgProject) return undefined;
    const organizationUuid = orgProject.organization_id;
    const slot =
      state.organizationProjects.provisioning?.[organizationUuid]?.[projectId];
    if (!slot) return undefined;
    const regional = state.project?.data?.[organizationId]?.[projectId];
    if (!regional) return undefined;
    // Defined (even as `{}`) means `loadEnvironments.fulfilled` ran for this
    // project. A legitimately empty env list must still clear the slot, or the
    // panel stays stuck on "provisioning" forever.
    const envs = state.environment?.data?.[organizationId]?.[projectId];
    if (envs === undefined) return undefined;
    return { organizationUuid, projectId };
  };

  startListening({
    actionCreator: loadOneProject.fulfilled,
    effect: (action, listenerApi) => {
      const payload = tryClearSlot(
        listenerApi.getState(),
        action.meta.arg.organizationId,
        action.meta.arg.projectId
      );
      if (payload) listenerApi.dispatch(clearProvisioningSlot(payload));
    }
  });

  startListening({
    actionCreator: loadEnvironments.fulfilled,
    effect: (action, listenerApi) => {
      const payload = tryClearSlot(
        listenerApi.getState(),
        action.meta.arg.organizationId,
        action.meta.arg.projectId
      );
      if (payload) listenerApi.dispatch(clearProvisioningSlot(payload));
    }
  });

  /**
   * A rejected regional/env fetch never writes `data`, so `tryClearSlot`'s
   * data-presence gates can't fire. Without these rejection listeners, a
   * transient 502 from the post-creation window leaves the panel pinned on
   * the all-done provisioning timeline until a manual refresh — clearing the
   * slot lets the phase selector fall through to its normal "no data" branch
   * (or `ProjectError` if `getProject` itself rejected).
   *
   * `action.meta.aborted` means the fetch was cancelled by the consumer
   * (page unmount), not the backend; the slot should survive that.
   */
  const findSlotForRejection = (
    state: RootState,
    organizationId: string | undefined,
    projectId: string | undefined
  ) => {
    if (!organizationId || !projectId) return undefined;
    const orgProject =
      state.organizationProjects.data?.[organizationId]?.[projectId];
    if (!orgProject) return undefined;
    const organizationUuid = orgProject.organization_id;
    const slot =
      state.organizationProjects.provisioning?.[organizationUuid]?.[projectId];
    if (!slot) return undefined;
    return { organizationUuid, projectId };
  };

  startListening({
    actionCreator: loadOneProject.rejected,
    effect: (action, listenerApi) => {
      if (action.meta.aborted) return;
      const { organizationId, projectId } = action.meta.arg;
      if (!organizationId || !projectId) return;

      const state = listenerApi.getState();

      // A retry loop is already managing this project — don't tear down its
      // slot or fork a parallel loop. The retry listener will clear the slot
      // itself if it exhausts.
      if (state.organizationProjects.regionalRetryInProgress?.[projectId]) {
        return;
      }

      // Post-creation race: org-scoped status flipped to `active` before the
      // regional cluster replicated. Hold the slot, schedule retries; let the
      // retry listener decide when to surface the error.
      const orgProject =
        state.organizationProjects.data?.[organizationId]?.[projectId];
      if (
        isRegionalNotFound(action.payload) &&
        orgProject?.status === OrganizationProjectStatusesTypes.active
      ) {
        listenerApi.dispatch(
          triggerPostProvisioningRegionalRetry({ organizationId, projectId })
        );
        return;
      }

      const payload = findSlotForRejection(state, organizationId, projectId);
      if (payload) listenerApi.dispatch(clearProvisioningSlot(payload));
    }
  });

  /**
   * Post-creation regional 404 recovery. Retries `loadOneProject` with a fixed
   * backoff while the org-scoped project remains `active` and the cluster keeps
   * 404ing. The slot is kept alive across attempts so `<ProjectError>` and the
   * phase selector keep painting the completed timeline instead of flashing
   * the error page. On any non-404, a status change away from `active`, or
   * after `REGIONAL_RETRY_MAX_ATTEMPTS`, the slot is cleared so the next
   * rejection cycles through the normal error path.
   */
  startListening({
    actionCreator: triggerPostProvisioningRegionalRetry,
    effect: async (action, listenerApi) => {
      // Per-project serialization is handled upstream by the
      // `regionalRetryInProgress` guard on `loadOneProject.rejected`.
      // `cancelActiveListeners()` would also kill loops for other projects,
      // orphaning their slots.
      const { organizationId, projectId } = action.payload;
      listenerApi.dispatch(markRegionalRetryStart({ projectId }));

      try {
        for (
          let attempt = 0;
          attempt < REGIONAL_RETRY_MAX_ATTEMPTS;
          attempt++
        ) {
          await listenerApi.delay(REGIONAL_RETRY_DELAY_MS);
          if (listenerApi.signal.aborted) return;

          const result = await listenerApi.dispatch(
            loadOneProject({ projectId, organizationId })
          );
          if (listenerApi.signal.aborted) return;

          if (loadOneProject.fulfilled.match(result)) return;

          const state = listenerApi.getState();
          const orgProject =
            state.organizationProjects.data?.[organizationId]?.[projectId];

          // Status moved off `active` (e.g. flipped back to `failed`). Clear the
          // slot before returning — the rejection listener won't fire again for
          // this loop (it returned early under the `regionalRetryInProgress`
          // guard), so leaving the slot alive would keep `ProjectError`
          // suppressing the error page until the user navigates away.
          if (orgProject?.status !== OrganizationProjectStatusesTypes.active) {
            const slotPayload = findSlotForRejection(
              listenerApi.getState(),
              organizationId,
              projectId
            );
            if (slotPayload) {
              listenerApi.dispatch(clearProvisioningSlot(slotPayload));
            }
            return;
          }

          // Different error — give up immediately so the user sees it.
          if (!isRegionalNotFound(result.payload)) {
            break;
          }
        }

        const slotPayload = findSlotForRejection(
          listenerApi.getState(),
          organizationId,
          projectId
        );
        if (slotPayload) {
          listenerApi.dispatch(clearProvisioningSlot(slotPayload));
        }
      } finally {
        listenerApi.dispatch(markRegionalRetryEnd({ projectId }));
      }
    }
  });

  startListening({
    actionCreator: loadEnvironments.rejected,
    effect: (action, listenerApi) => {
      if (action.meta.aborted) return;
      const payload = findSlotForRejection(
        listenerApi.getState(),
        action.meta.arg.organizationId,
        action.meta.arg.projectId
      );
      if (payload) listenerApi.dispatch(clearProvisioningSlot(payload));
    }
  });

  /**
   * Single listener handles both directions of the activity-seen flag:
   * - `isLoading=true`: mark seen immediately, cancel any pending clear.
   * - `isLoading=false`: wait 10s, then clear (cancellable if a new mark
   *   arrives within the window).
   */
  startListening({
    actionCreator: setIntegrationActivityState,
    effect: async (action, listenerApi) => {
      listenerApi.cancelActiveListeners();

      const { projectId, isLoading } = action.payload;

      if (isLoading) {
        listenerApi.dispatch(markIntegrationActivitiesSeen({ projectId }));
        return;
      }

      if (
        !listenerApi.getState().organizationProjects.seenIntegrationActivities[
          projectId
        ]
      ) {
        return;
      }

      await listenerApi.delay(ACTIVITY_DEBOUNCE_MS);
      if (listenerApi.signal.aborted) return;

      listenerApi.dispatch(clearIntegrationActivitiesSeen({ projectId }));
    }
  });
};

// Register against the production middleware singleton.
registerProjectsListeners(startAppListening);
